/**
 * Firebase Auth wrappers.
 *
 * Thin convenience functions around the modular Firebase Auth SDK.  All
 * functions use the singleton `auth` instance exported from `./client.ts`.
 */

import {
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithCustomToken as fbSignInWithCustomToken,
  onAuthStateChanged,
  type UserCredential,
  type User,
} from 'firebase/auth';
import { auth } from './client';
import { clearUserBooks } from '$lib/services/BookFileStore';

/**
 * Set auth persistence to `browserLocalPersistence` so the session survives
 * page reloads.
 */
export function initAuthPersistence(): Promise<void> {
  if (!auth) return Promise.reject(new Error('Firebase Auth not initialised'));
  return setPersistence(auth, browserLocalPersistence);
}

/** Sign in with email and password. */
export function signInWithEmail(email: string, password: string): Promise<UserCredential> {
  if (!auth) return Promise.reject(new Error('Firebase Auth not initialised'));
  return signInWithEmailAndPassword(auth, email, password);
}

/** Sign in with a Firebase Custom Token (used by passkey flow). */
export function signInWithCustomToken(token: string): Promise<UserCredential> {
  if (!auth) return Promise.reject(new Error('Firebase Auth not initialised'));
  return fbSignInWithCustomToken(auth, token);
}

/** Create a new user account with email and password. */
export function signUpWithEmail(email: string, password: string): Promise<UserCredential> {
  if (!auth) return Promise.reject(new Error('Firebase Auth not initialised'));
  return createUserWithEmailAndPassword(auth, email, password);
}

/** Sign out the current user and delete the books stored on this device for them. */
export async function signOutUser(): Promise<void> {
  if (!auth) throw new Error('Firebase Auth not initialised');
  const uid = auth.currentUser?.uid;
  if (uid) await clearUserBooks(uid).catch((e) => console.error('Failed to delete stored books:', e));
  return signOut(auth);
}

/** Send a password-reset email via the custom backend endpoint. */
export function resetPassword(email: string): Promise<void> {
  return fetch('/api/v1/auth/send-password-reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  }).then((response) => {
    if (!response.ok) throw new Error(response.statusText);
  });
}

/**
 * Send an email-verification link via the custom backend endpoint.
 * The backend route requires the Firebase ID token (added by BaseApiService).
 */
export async function verifyEmail(): Promise<void> {
  if (!auth?.currentUser) return Promise.reject(new Error('No authenticated user'));
  const { BaseApiService } = await import('$lib/services/ApiService');
  const api = await BaseApiService.getInstance();
  await api.execute('/api/v1/auth/send-verification-email', {
    method: 'POST',
    body: JSON.stringify({ uid: auth.currentUser.uid }),
  });
}

/**
 * Subscribe to auth state changes.
 *
 * @returns A cleanup function that unsubscribes the listener.
 */
export function onAuthChanged(callback: (user: User | null) => void): () => void {
  if (!auth) {
    console.warn('Firebase Auth not initialised — onAuthChanged is a no-op');
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}
